import React, { useState, useRef, useEffect, useMemo } from "react";
import "../../styles/Dashboard.css";
import { saveAs } from "file-saver";
import axios from "axios";
import { base_url } from "../../utils/apiConfig";
import { debounce } from "lodash";
import ScrollDown from "../../components/ScrollDown";
import { Menu, Spin } from "antd";
import UpcomingAuditModal from "../../components/modals/UpcomingAuditModal";
import QuestionSummary from "./question-summary/QuestionSummary";
import SectionSummary from "./section-summary/SectionSummary";
import NPSSection from "./nps/NPSSection";
import Branchperformance from "./branch-performance/Branchperformance";
import DashBoardFilters from "./DashBoardFilters";
import ImprovementAreas from "./improvement-areas/ImprovementAreas";
import OverallView from "./overall-view/OverallView";
import { toBlob } from "html-to-image";
import { freezeLayout } from "../../utils/helper/exportFonts";
import { useFetchFunction } from "../../utils/hooks/UseFetchUrl";

const STORAGE_KEYS = {
  questionnaire: "dashboard_selected_questionnaire",
  cycles: "dashboard_selected_cycles",
};


const Dashboard = () => {
  const { loading: queTypesLoading, data: queTypesData, getData: getQueTypesData } = useFetchFunction();
  const { loading: cyclesLoading, data: cyclesData, getData: getCyclesData } = useFetchFunction();
  const { loading: dashboardLoading, data: dashboardData, getData: getDashboardData } = useFetchFunction();
  const { loading: overallLoading, data: overallData, getData: getOverallData } = useFetchFunction();
  const { loading: npsLoading, data: npsData, getData: getNpsData } = useFetchFunction();
  const { loading: ssLoading, data: ssData, getData: getSSData } = useFetchFunction();
  const { loading: improLoading, data: improData, getData: getImproData } = useFetchFunction();
  const { loading: storeTrendsLoading, data: storeTrendsData, getData: getStoreTrendsData } = useFetchFunction();
  const { loading: cityTrendsLoading, data: cityTrendsData, getData: getCityTrendsData } = useFetchFunction();
  const { loading: qsLoading, data: qsData, getData: getQSData } = useFetchFunction();
  const { data: lastStoreData, getData: getLastStoreData } = useFetchFunction();
  const { loading: auditStoreIdPassLoading, data: auditStoreIdPass, getData: getAuditStoreIdPass } = useFetchFunction();

  // ==============================================  States =================================================
  const [downloadingSection, setDownloadingSection] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [selectedAuditDate, setSelectedAuditDate] = useState(null);
  const [selectedDateAudits, setSelectedDateAudits] = useState([]);
  const [selectedTypeLabel, setSelectedTypeLabel] = useState("");
  const [selectedCycleLabel, setSelectedCycleLabel] = useState("");
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [cycleMenuOpen, setCycleMenuOpen] = useState(false);
  const [showUpAuditsModal, setShowUpAuditsModal] = useState(false);
  const [selectedQueId, setSelectedQueId] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.questionnaire);
      return saved ? Number(saved) : null;
    } catch {
      return null;
    }
  });
  const [selectedCycleIds, setSelectedCycleIds] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.cycles);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const dynamicId = lastStoreData?.stores_list?.[0]?.id;
  const lastDashboardQueryRef = useRef(null);
  const cycleDropdownRef = useRef(null);

  const fetchApis = useMemo(
    () =>
      debounce((selectedQueId, cycleIds) => {
        if (!selectedQueId || !cycleIds?.length) return;

        const auditCycleIds = cycleIds.join(",");
        getNpsData(`over_all_nps_score?questionnaire_type_id=${selectedQueId}&audit_cycle_ids=${auditCycleIds}`); // NPS data
        getSSData(`dashboard/questionnaire_type/${selectedQueId}/time_series?audit_cycle_ids=${auditCycleIds}`); // Section summary data
        getImproData(`dashboard/questionnaire_type/${selectedQueId}/improvable_questions?audit_cycle_ids=${auditCycleIds}`);  // Improvement areas
        getStoreTrendsData(`dashboard/questionnaire_type/${selectedQueId}/store_trends?audit_cycle_ids=${auditCycleIds}`); // store wise data
        getCityTrendsData(`dashboard/questionnaire_type/${selectedQueId}/city_trends?audit_cycle_ids=${auditCycleIds}`); // city wise data
        getQSData(`dashboard/questionnaire_type/${selectedQueId}/questionnaire_survey?audit_cycle_ids=${auditCycleIds}`); //questionnaire summary data
      }, 500),
    []
  );

  const widgetAccess = useMemo(() => ({
    overall_audit_cycle_score: dashboardData?.overall_audit_cycle_score ?? true,
    upcoming_audits: dashboardData?.upcoming_audits ?? true,
    net_promoter_score: dashboardData?.net_promoter_score ?? true,
    section_summary: dashboardData?.section_summary ?? true,
    improvement_areas_based_on_observation: dashboardData?.improvement_areas_based_on_observation ?? true,
    branch_performance: dashboardData?.branch_performance ?? true,
    store_wise_performance: dashboardData?.store_wise_performance ?? true,
    city_wise_performance: dashboardData?.city_wise_performance ?? true,
    questionnaire_summary: dashboardData?.questionnaire_summary ?? true,
  }),
    [dashboardData]
  );

  // ============================================== useEffects =================================================
  useEffect(() => {
    const handleClickOutsideCycle = (event) => {
      if (
        cycleDropdownRef.current &&
        !cycleDropdownRef.current.contains(event.target)
      ) {
        setCycleMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideCycle);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideCycle);
  }, []);

  useEffect(() => {
    if (!selectedQueId) return;
    try {
      localStorage.setItem(STORAGE_KEYS.questionnaire, String(selectedQueId));
    } catch {
      // storage unavailable — ignore, selection just won't persist this session
    }
  }, [selectedQueId]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.cycles, JSON.stringify(selectedCycleIds));
    } catch {
      // storage unavailable — ignore
    }
  }, [selectedCycleIds]);

  useEffect(() => {
    if (!selectedQueId) return;

    getOverallData(
      `dashboard/questionnaire_type/${selectedQueId}/audit_cycle_scores`,
    );
  }, [selectedQueId]);

  useEffect(() => {
    getLastStoreData("store?lastStoreId=");
    getDashboardData("client/dashboard_widget_access");

    getCyclesData("audit_cycle_for_dashboard");
    getQueTypesData("questionnaire_types_for_dashboard");
  }, []);

  useEffect(() => {
    if (!selectedQueId || selectedCycleIds.length === 0) {
      fetchApis.cancel();
      return;
    }

    const queryKey = `${selectedQueId}:${selectedCycleIds.join(",")}`;
    if (lastDashboardQueryRef.current === queryKey) return;

    lastDashboardQueryRef.current = queryKey;
    fetchApis(selectedQueId, selectedCycleIds);

    return () => {
      fetchApis.cancel();
    };
  }, [selectedQueId, selectedCycleIds, fetchApis]);

  useEffect(() => {
    return () => fetchApis.cancel();
  }, [fetchApis]);

  useEffect(() => {
    if (!dynamicId) {
      console.error("queDataListId is undefined. Skipping API call.");
      return;
    }

    getAuditStoreIdPass(`store/${dynamicId}/audit_store`);
  }, [dynamicId]);

  useEffect(() => {
    if (!queTypesData?.length) return;

    setSelectedQueId((prev) => {
      if (prev && queTypesData.some((q) => q.id === prev)) {
        return prev;
      }

      return queTypesData[0].id;
    });
  }, [queTypesData]);

  useEffect(() => {
    if (!cyclesData?.length || !selectedQueId) {
      setDetailsData([]);
      return;
    }

    const filteredCycles = cyclesData.filter(
      (cycle) => cycle.questionnaire_type.id === selectedQueId
    );

    setDetailsData(filteredCycles);
    setSelectedCycleIds((prev) => {
      const validSelectedCycles = prev.filter((id) =>
        filteredCycles.some((cycle) => cycle.id === id)
      );

      if (validSelectedCycles.length > 0) {
        const unchanged =
          prev.length === validSelectedCycles.length &&
          prev.every((id, index) => id === validSelectedCycles[index]);

        return unchanged ? prev : validSelectedCycles;
      }

      return filteredCycles.length > 0 ? [filteredCycles[0].id] : [];
    });
  }, [cyclesData, selectedQueId]);

  useEffect(() => {
    const match = queTypesData?.find((item) => item.id === selectedQueId);
    if (match) setSelectedTypeLabel(match.name);
  }, [queTypesData, selectedQueId]);

  useEffect(() => {
    const selectedCycles = detailsData?.filter((item) => selectedCycleIds.includes(item.id));

    setSelectedCycleLabel(
      selectedCycles?.length
        ? selectedCycles.map((item) => item.name).join(", ")
        : ""
    );
  }, [detailsData, selectedCycleIds]);

  // ============================================== Functions =================================================
  const typeMenu = (
    <Menu
      onClick={({ key }) => {
        const selectedId = parseInt(key, 10);
        setSelectedQueId(selectedId);
        const match = queTypesData?.find((item) => item.id === selectedId);
        if (match) setSelectedTypeLabel(match.name);
      }}
      selectedKeys={[String(selectedQueId)]}
    >
      {queTypesData?.map((item) => (
        <Menu.Item key={item.id}>{item.name}</Menu.Item>
      ))}
    </Menu>
  );

  const downloadBarChartAsPNG = async (chartRef, section) => {
    const card = chartRef.current;
    if (!card) return;

    setDownloadingSection(section);
    let restoreLayout;

    try {
      await document.fonts?.ready;

      restoreLayout = freezeLayout(card);
      const blob = await toBlob(card, {
        pixelRatio: 1,
        backgroundColor: "#ffffff",
        skipFonts: true,
        cacheBust: false,
        filter: (node) => !node.classList?.contains("no-print"),
      });

      if (!blob) {
        throw new Error("Failed to generate image");
      }

      const selectedCycleNames = detailsData
        .filter((cycle) => selectedCycleIds.includes(cycle.id))
        .map((cycle) => cycle.name);

      const formattedCycleName =
        selectedCycleNames.length > 0
          ? selectedCycleNames.join("_").replace(/\s+/g, "")
          : "dashboard";

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${formattedCycleName}_${section}.png`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Error downloading PNG:", error);
    } finally {
      restoreLayout?.();
      setDownloadingSection(false);
    }
  };

  const downloadImprovableTable = async (section) => {
    setDownloadingSection(section);
    const token = localStorage.getItem("authToken");
    const apiUrl = `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/audit_cycle_improvable_questions_xlsx?questionnaire_type_id=${selectedQueId}&audit_cycle_ids=${selectedCycleIds.join(",")}`;

    try {
      const response = await axios.get(apiUrl, {
        responseType: "blob",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const selectedCycleNames = detailsData.filter((cycle) => selectedCycleIds.includes(cycle.id)).map((cycle) => cycle.name);
      const selectedCycleName = selectedCycleNames.length > 0 ? selectedCycleNames.join("_") : "Improvement area based details";
      const formattedCycleName = selectedCycleName.replace(/\s+/g, "");

      saveAs(blob, `${formattedCycleName} Improvable Questions List.xlsx`);
      setDownloadingSection(section);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setDownloadingSection(false);
    }
  };

  const downloadQuestionSummaryTable = async (section) => {
    setDownloadingSection(section);
    const token = localStorage.getItem("authToken");
    const apiUrl =
      `${base_url.protocol}://${base_url.hostname}:${base_url.port}` +
      `${base_url.api_base_path}` +
      `client/audit_cycle_questionnaire_survey_xlsx` +
      `?questionnaire_type_id=${selectedQueId}` +
      `&audit_cycle_ids=${selectedCycleIds.join(",")}`;

    try {
      const response = await axios.get(apiUrl, {
        responseType: "blob",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(
        blob,
        `Questions summary List.xlsx`
      );
      setDownloadingSection(section);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setDownloadingSection(false);
    }
  };

  const handleAuditDateClick = (dateStr, audits) => {
    setSelectedAuditDate(dateStr);
    setSelectedDateAudits(audits);
    setShowUpAuditsModal(true);
  };

  if (dashboardLoading || queTypesLoading || cyclesLoading) {
    return <div className="dashboard-loader">
      <Spin size="large" />
    </div>
  }

  return (
    <>
      <div className="heroDashboard">
        <DashBoardFilters
          typeMenu={typeMenu}
          setTypeMenuOpen={setTypeMenuOpen}
          typeMenuOpen={typeMenuOpen}
          selectedTypeLabel={selectedTypeLabel}
          cycleDropdownRef={cycleDropdownRef}
          cycleMenuOpen={cycleMenuOpen}
          setCycleMenuOpen={setCycleMenuOpen}
          selectedCycleLabel={selectedCycleLabel}
          detailsData={detailsData}
          selectedCycleIds={selectedCycleIds}
          setSelectedCycleIds={setSelectedCycleIds}
        />

        {(widgetAccess.overall_audit_cycle_score || widgetAccess.upcoming_audits) && (
          <OverallView
            overallLoading={overallLoading}
            selectedQueId={selectedQueId}
            scoresData={overallData}
            handleAuditDateClick={handleAuditDateClick}
            showOverallScore={widgetAccess.overall_audit_cycle_score}
            showUpcomingAudits={widgetAccess.upcoming_audits}
          />)}

        {widgetAccess.net_promoter_score && (
          <NPSSection
            cycleIds={selectedCycleIds}
            cycles={detailsData}
            npsLoading={npsLoading}
            npsData={npsData}
            downloading={downloadingSection === "nps"}
            downloadBarChartAsPNG={downloadBarChartAsPNG}
          />
        )}

        {widgetAccess.section_summary && (
          <SectionSummary
            cycleIds={selectedCycleIds}
            cycles={detailsData}
            ssLoading={ssLoading}
            SectionSummaryData={ssData}
            downloading={downloadingSection === "section-summary"}
            downloadBarChartAsPNG={downloadBarChartAsPNG}
          />
        )}

        {widgetAccess.improvement_areas_based_on_observation && (
          <ImprovementAreas
            cycleIds={selectedCycleIds}
            cycles={detailsData}
            impLoading={improLoading}
            data={improData}
            downloading={downloadingSection === "improvement-areas"}
            downloadImprovableTable={downloadImprovableTable}
          />
        )}

        {widgetAccess.branch_performance && (
          <Branchperformance
            cycleIds={selectedCycleIds}
            cycles={detailsData}
            isLoading={auditStoreIdPassLoading}
            stData={storeTrendsData}
            storeTrendsLoading={storeTrendsLoading}
            cityData={cityTrendsData}
            cityTrendsLoading={cityTrendsLoading}
            auditStoreIdPass={auditStoreIdPass}
            downloading={downloadingSection === "branch-performance"}
            downloadBarChartAsPNG={downloadBarChartAsPNG}
            showStorePerformance={widgetAccess.store_wise_performance}
            showCityPerformance={widgetAccess.city_wise_performance}
          />
        )}


        {widgetAccess.questionnaire_summary && (
          <QuestionSummary
            cycleIds={selectedCycleIds}
            cycles={detailsData}
            qsLoading={qsLoading}
            data={qsData}
            downloading={downloadingSection === "questionnaire-summary"}
            downloadQuestionSummaryTable={downloadQuestionSummaryTable}
          />
        )}
      </div>


      {/* ===================================== Modals ======================================= */}
      <UpcomingAuditModal
        open={showUpAuditsModal}
        onClose={() => setShowUpAuditsModal(false)}
        upAudsData={selectedDateAudits}
        selectedAuditDate={selectedAuditDate}
      />
      <ScrollDown />
    </>
  );
};

export default Dashboard;